import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, useEffect, useState } from 'react';
import { FreeMode, Keyboard, Navigation, Thumbs, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/zoom';
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react';

export interface GalleryImage {
  src: string;
  alt?: string;
  title?: string;
}

interface GalleryProps {
  images: GalleryImage[];
  height?: number | string;
  width?: number | string;
  style?: React.CSSProperties;
}

export const Gallery = ({
  images,
  height = 220,
  width = '100%',
  style,
}: GalleryProps): ReactElement => {
  return (
    <SwiperReact
      style={{ width, height, ...style }}
      spaceBetween={8}
      slidesPerView={Math.min(4, images.length)}
      navigation={true}
      modules={[Navigation]}
    >
      {images.map((img, idx) => (
        <SwiperSlide
          key={idx}
          style={{
            borderRadius: '4px',
            overflow: 'hidden',
            border: '1px solid var(--ion-border-color)',
            background:
              'var(--ion-item-background, var(--ion-background-color))',
          }}
        >
          <img
            src={img.src}
            alt={img.alt || ''}
            title={img.title || img.alt || ''}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </SwiperSlide>
      ))}
    </SwiperReact>
  );
};

export interface GalleryModalProps {
  images: GalleryImage[];
  open: boolean;
  initialIndex?: number;
  onClose: () => void;
}

export const GalleryModal = ({
  images,
  open,
  initialIndex = 0,
  onClose,
}: GalleryModalProps): ReactElement => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const currentImage = images[activeIndex];

  // Cleanup thumbsSwiper when modal closes
  useEffect(() => {
    if (!open && thumbsSwiper) {
      thumbsSwiper.destroy();
      setThumbsSwiper(null);
    }
  }, [open, thumbsSwiper]);

  // Reset active index when modal opens
  useEffect(() => {
    if (open) {
      setActiveIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const handleClose = () => {
    if (thumbsSwiper) {
      thumbsSwiper.destroy();
      setThumbsSwiper(null);
    }
    onClose();
  };

  return (
    <IonModal
      isOpen={open}
      onDidDismiss={handleClose}
      style={{
        '--height': '100%',
        '--width': '100%',
        '--background': 'var(--ion-background-color)',
      }}
    >
      <IonHeader>
        <IonToolbar
          style={{
            '--background': 'var(--ion-toolbar-background, rgba(0, 0, 0, 0.4))',
          }}
        >
          <IonButtons slot="start">
            <IonButton onClick={handleClose}>
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>
            {currentImage?.title || `${activeIndex + 1} / ${images.length}`}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <div
        style={{ height: '100%', background: 'var(--ion-background-color)' }}
      >
        <SwiperReact
          style={{
            width: '100%',
            height: '80vh',
          }}
          initialSlide={initialIndex}
          modules={[FreeMode, Navigation, Thumbs, Zoom, Keyboard]}
          keyboard={{ enabled: true, pageUpDown: true }}
          navigation={true}
          thumbs={{ swiper: thumbsSwiper }}
          zoom={true}
          onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        >
          {images.map((img, idx) => (
            <SwiperSlide key={idx}>
              <div className="swiper-zoom-container">
                <img
                  src={img.src}
                  alt={img.alt || ''}
                  title={img.title || img.alt || ''}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '75vh',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </SwiperReact>
        <SwiperReact
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '100px',
            padding: '10px 0',
            background: 'var(--ion-toolbar-background, rgba(0, 0, 0, 0.4))',
          }}
          onSwiper={setThumbsSwiper}
          initialSlide={initialIndex}
          spaceBetween={8}
          slidesPerView={Math.min(6, images.length)}
          centeredSlides={true}
          centeredSlidesBounds={true}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
        >
          {images.map((img, idx) => (
            <SwiperSlide
              key={idx}
              style={{
                opacity: activeIndex === idx ? 1 : 0.6,
                transition: 'opacity 0.3s ease',
              }}
            >
              <img
                src={img.src}
                alt={img.alt || ''}
                title={img.title || img.alt || ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border:
                    activeIndex === idx
                      ? '2px solid var(--ion-color-primary)'
                      : 'none',
                }}
                onClick={() => thumbsSwiper?.slideTo(idx)}
              />
            </SwiperSlide>
          ))}
        </SwiperReact>
      </div>
    </IonModal>
  );
};

export interface GalleryThumbnailsProps {
  images: GalleryImage[];
  size?: number;
  style?: React.CSSProperties;
}

export const GalleryThumbnails = ({
  images,
  size = 72,
  style,
}: GalleryThumbnailsProps): ReactElement => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleClick = (idx: number) => {
    setSelectedIndex(idx);
    setModalOpen(true);
  };

  return (
    <>
      <SwiperReact
        style={{
          ...style,
          padding: '4px',
        }}
        spaceBetween={8}
        slidesPerView={Math.min(6, images.length)}
        modules={[Navigation]}
        navigation={true}
      >
        {images.map((img, idx) => (
          <SwiperSlide
            key={idx}
            onClick={() => handleClick(idx)}
            style={{
              width: size,
              height: size,
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: '1px solid var(--ion-border-color)',
            }}
          >
            <img
              src={img.src}
              alt={img.alt || ''}
              title={img.title || img.alt || ''}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </SwiperSlide>
        ))}
      </SwiperReact>
      <GalleryModal
        images={images}
        open={modalOpen}
        initialIndex={selectedIndex}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
